<template>
    <div v-if="isLoading" class="loading-overlay">
        <p>Carregando...</p>
    </div>

    <div
        v-if="msgAlerta"
        class="info-alert row-space clickable"
        @click="msgAlerta = null"
    >
        <span class="text">{{ msgAlerta }}</span>
    </div>

    <div class="os-page">
        <!-- Barra superior -->
        <div class="page-toolbar">
            <div>
                <h2>Ordem de Serviço #{{ objView.cdipos || props.idView }}</h2>
                <p class="page-subtitle">Detalhes da execução da ordem de serviço</p>
            </div>

            <div class="toolbar-actions">
                <button
                    v-if="lsAnexos.length > 0"
                    type="button"
                    class="btn-small btn-photos"
                    @click="abrirFotos"
                >
                    Fotos ({{ lsAnexos.length }})
                </button>

                <button
                    type="button"
                    class="btn-small"
                    @click="closeForm"
                >
                    Voltar
                </button>
            </div>
        </div>

        <!-- Resumo compacto da ordem de serviço -->
        <section class="os-summary-card">
            <div class="summary-main">
                <div class="service-summary">
                    <span class="summary-label">Serviço</span>

                    <div class="service-title-row">
                        <h3>{{ objView.nmipservico || 'Serviço não informado' }}</h3>

                        <span v-if="objView.statusnm" class="status-chip">
                            {{ objView.statusnm }}
                        </span>
                    </div>
                </div>

                <div class="summary-metrics">
                    <div class="metric-item">
                        <span class="summary-label">Código</span>
                        <strong>#{{ objView.cdipos ?? '-' }}</strong>
                    </div>

                    <div class="metric-item">
                        <span class="summary-label">Data da OS</span>
                        <strong>{{ Funcoes.formatDateFromJson(objView.dtipos) }}</strong>
                    </div>

                    <div class="metric-item">
                        <span class="summary-label">Quantidade</span>
                        <strong>{{ Funcoes.formatDecimal(objView.qtd_itens, 1) }}</strong>
                    </div>

                    <div class="metric-item metric-total">
                        <span class="summary-label">Valor total</span>
                        <strong>{{ Funcoes.formatDecimal(objView.vltotal) }}</strong>
                    </div>
                </div>
            </div>

            <div class="summary-meta-grid">
                <div class="meta-item">
                    <span class="summary-label">Prestador / fornecedor</span>
                    <strong>{{ objView.nmfornecedor || '-' }}</strong>
                </div>

                <div class="meta-item">
                    <span class="summary-label">Localização</span>

                    <div class="location-content">
                        <strong>{{ enderecoFormatado }}</strong>

                        <a
                            v-if="possuiCoordenadas(objView)"
                            :href="googleMapsUrl(objView.latitude, objView.longitude)"
                            target="_blank"
                            rel="noopener noreferrer"
                            class="map-link"
                        >
                            Abrir no Google Maps
                        </a>
                    </div>

                    <small v-if="possuiCoordenadas(objView)" class="coordinates">
                        {{ formatarCoordenada(objView.latitude) }},
                        {{ formatarCoordenada(objView.longitude) }}
                    </small>
                </div>

                <div class="meta-item">
                    <span class="summary-label">Autorização</span>
                    <strong>{{ formatarDataHora(objView.dhautorizacao) }}</strong>
                </div>

                <div class="meta-item execution-period">
                    <span class="summary-label">Execução</span>

                    <div class="execution-dates">
                        <div>
                            <small>Início</small>
                            <strong>{{ formatarDataHoraSeparada(objView.dtinicio, objView.hrinicio) }}</strong>
                        </div>

                        <span class="execution-arrow">→</span>

                        <div>
                            <small>Término</small>
                            <strong>{{ formatarDataHoraSeparada(objView.dtfim, objView.hrfim) }}</strong>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <!-- Eventos e itens -->
        <div class="content-columns">
            <!-- Coluna de eventos -->
            <section class="content-card">
                <div class="card-header">
                    <div>
                        <h3>Eventos</h3>
                        <p>Histórico da ordem de serviço</p>
                    </div>

                    <span class="count-badge">{{ lsEventos.length }}</span>
                </div>

                <div v-if="lsEventos.length === 0" class="empty-state">
                    Nenhum evento registrado.
                </div>

                <div v-else class="timeline">
                    <article
                        v-for="(evento, index) in lsEventos"
                        :key="evento.id || index"
                        class="timeline-item"
                    >
                        <div class="timeline-marker">
                            <span class="timeline-dot"></span>
                            <span
                                v-if="index < lsEventos.length - 1"
                                class="timeline-line"
                            ></span>
                        </div>

                        <div class="event-card">
                            <div class="event-header">
                                <strong>{{ evento.tpeventonm || 'Evento' }}</strong>

                                <span class="event-date">
                                    {{ formatarDataHoraSeparada(
                                        evento.dtevento,
                                        evento.hrevento
                                    ) }}
                                </span>
                            </div>

                            <div class="event-details">
                                <div>
                                    <span class="event-label">Operador</span>
                                    <span>{{ evento.nmoperador || '-' }}</span>
                                </div>

                                <a
                                    v-if="possuiCoordenadas(evento)"
                                    :href="googleMapsUrl(
                                        evento.latitude,
                                        evento.longitude
                                    )"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    class="map-link"
                                >
                                    Ver localização
                                </a>
                            </div>
                        </div>
                    </article>
                </div>
            </section>

            <!-- Coluna de itens -->
            <section class="content-card">
                <div class="card-header">
                    <div>
                        <h3>Itens utilizados</h3>
                        <p>Produtos aplicados na execução</p>
                    </div>

                    <span class="count-badge">{{ lsItens.length }}</span>
                </div>

                <div v-if="lsItens.length === 0" class="empty-state">
                    Nenhum item registrado.
                </div>

                <div v-else class="items-table-wrapper">
                    <table class="items-table">
                        <thead>
                            <tr>
                                <th class="column-code">Código</th>
                                <th>Produto</th>
                                <th class="column-number">Quantidade</th>
                                <th class="column-money">Valor unitário</th>
                                <th class="column-money">Total</th>
                            </tr>
                        </thead>

                        <tbody>
                            <tr
                                v-for="(item, index) in lsItens"
                                :key="item.id || index"
                            >
                                <td>
                                    <span class="product-code">
                                        {{ item.cdproduto ?? '-' }}
                                    </span>
                                </td>

                                <td>
                                    <strong class="product-name">
                                        {{ item.nmproduto || '-' }}
                                    </strong>
                                </td>

                                <td class="column-number">
                                    {{ Funcoes.formatDecimal(item.qtd, 1) }}
                                </td>

                                <td class="column-money">
                                    {{ Funcoes.formatDecimal(item.vlunitario) }}
                                </td>

                                <td class="column-money item-total">
                                    {{ Funcoes.formatDecimal(item.vltotal) }}
                                </td>
                            </tr>
                        </tbody>

                        <tfoot>
                            <tr>
                                <td colspan="2">Totais</td>
                                <td class="column-number">
                                    {{ Funcoes.formatDecimal(totalQuantidadeItens, 1) }}
                                </td>
                                <td></td>
                                <td class="column-money">
                                    {{ Funcoes.formatDecimal(totalValorItens) }}
                                </td>
                            </tr>
                        </tfoot>
                    </table>
                </div>
            </section>
        </div>
    </div>

    <div v-if="modalFotosAberto" class="modal-overlay">
        <div class="photos-modal">
            <div class="modal-header">
                <h3>Fotos da Solicitação e Execução</h3>
                <button class="btn-small" @click="modalFotosAberto = false">FECHAR</button>
            </div>

            <div v-if="loadingFotos" class="photos-loading">
                Carregando fotos...
            </div>

            <div v-else class="photos-grid">
                <img
                    v-for="(foto, index) in fotosCarregadas"
                    :key="foto.idanexo"
                    :src="foto.url"
                    class="photo-thumb"
                    :class="{ 'photo-thumb-active': index === fotoAtualIndex }"
                    @click="fotoAtualIndex = index"
                />
            </div>

            <div v-if="fotosCarregadas[fotoAtualIndex]" class="photo-preview">
                <img :src="fotosCarregadas[fotoAtualIndex].url" />
            </div>
        </div>
    </div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue';
import { Api } from '@/funcoes/apiExport';
import { Funcoes } from '@/funcoes/utils';
import { globalStore } from '@/globals';

const emit = defineEmits(['close']);

const props = defineProps({
    idView: {
        type: Number,
        default: 0
    }
});

const isLoading = ref(false);
const msgAlerta = ref(null);

const objView = ref({});
const lsAnexos = ref([]);
const lsEventos = ref([]);
const lsItens = ref([]);

const modalFotosAberto = ref(false);
const loadingFotos = ref(false);
const fotosCarregadas = ref([]);
const fotoAtualIndex = ref(0);

const enderecoFormatado = computed(() => {
    const partes = [
        objView.value.nmlogradouro,
        objView.value.nmbairro
    ].filter(Boolean);

    return partes.length > 0
        ? partes.join(' — ')
        : 'Endereço não informado';
});

const totalQuantidadeItens = computed(() => {
    return lsItens.value.reduce((total, item) => {
        return total + numeroSeguro(item.qtd);
    }, 0);
});

const totalValorItens = computed(() => {
    return lsItens.value.reduce((total, item) => {
        return total + numeroSeguro(item.vltotal);
    }, 0);
});


function closeForm() {
    emit('close');
}

function numeroSeguro(valor) {
    const numero = Number(valor);
    return Number.isFinite(numero) ? numero : 0;
}

function formatarData(data) {
    if (!data) return '-';

    const somenteData = String(data).substring(0, 10);
    const partes = somenteData.split('-');

    if (partes.length !== 3) return data;

    return `${partes[2]}/${partes[1]}/${partes[0]}`;
}

function formatarHora(hora) {
    if (!hora) return '';

    return String(hora).substring(0, 5);
}

function formatarDataHora(dataHora) {
    if (!dataHora) return '-';

    const texto = String(dataHora);
    const data = texto.substring(0, 10);
    const hora = texto.includes('T')
        ? texto.split('T')[1]?.substring(0, 5)
        : '';

    return [formatarData(data), hora].filter(Boolean).join(' às ');
}

function formatarDataHoraSeparada(data, hora) {
    if (!data && !hora) return '-';

    const dataFormatada = formatarData(data);
    const horaFormatada = formatarHora(hora);

    if (data && horaFormatada) {
        return `${dataFormatada} às ${horaFormatada}`;
    }

    return data ? dataFormatada : horaFormatada;
}


function formatarCoordenada(valor) {
    const coordenada = Number(valor);

    if (!Number.isFinite(coordenada)) return '-';

    return coordenada.toFixed(6);
}

function possuiCoordenadas(registro) {
    if (!registro) return false;

    const latitude = Number(registro.latitude);
    const longitude = Number(registro.longitude);

    return (
        Number.isFinite(latitude) &&
        Number.isFinite(longitude) &&
        latitude >= -90 &&
        latitude <= 90 &&
        longitude >= -180 &&
        longitude <= 180 &&
        !(latitude === 0 && longitude === 0)
    );
}

function googleMapsUrl(latitude, longitude) {
    if (!possuiCoordenadas({ latitude, longitude })) {
        return '#';
    }

    const coordenadas = `${Number(latitude)},${Number(longitude)}`;

    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
        coordenadas
    )}`;
}

async function abrirFotos() {
    modalFotosAberto.value = true;

    if (fotosCarregadas.value.length > 0) {
        return;
    }

    loadingFotos.value = true;

    try {
        const resultados = await Promise.all(
            lsAnexos.value.map(async (anexo) => {
                try {
                    const rUrl = await Api.genericPost(
                        '/main/solicitar/download',
                        {
                            id: anexo.idanexo
                        }
                    );

                    if (rUrl.bolok !== 1) {
                        return null;
                    }

                    return {
                        ...anexo,
                        url: rUrl.download_url,
                        nome: rUrl.nome_arquivo
                    };
                } catch {
                    return null;
                }
            })
        );

        fotosCarregadas.value = resultados.filter(Boolean);
        fotoAtualIndex.value = 0;
    } catch (error) {
        msgAlerta.value = `ATENÇÃO: ${
            error?.message || 'Não foi possível carregar as fotos.'
        }`;
    } finally {
        loadingFotos.value = false;
    }
}

async function lerDados() {
    try {
        isLoading.value = true;

        const [req, rEventos, rItens, rAnexos] = await Promise.all([
            Api.genericPost('/ip/ipos/full', {
                idmunicipio: globalStore.idmunicipio,
                id: props.idView
            }),

            Api.genericPost('/ip/ipos/eventos', {
                idos: props.idView
            }),

            Api.genericPost('/ip/ipos/itens', {
                idos: props.idView
            }),

            Api.genericPost('/ip/ipos/anexos', {
                idos: props.idView
            })
        ]);

        objView.value = req?.[0] || {};
        lsEventos.value = Array.isArray(rEventos) ? rEventos : [];
        lsItens.value = Array.isArray(rItens) ? rItens : [];
        lsAnexos.value = Array.isArray(rAnexos) ? rAnexos : [];
    } catch (error) {
        msgAlerta.value =
            error?.message ||
            error ||
            'Não foi possível carregar os dados da ordem de serviço.';
    } finally {
        isLoading.value = false;
    }
}

onMounted(lerDados);
</script>

<style scoped>
.os-page {
    --primary: #2563eb;
    --primary-light: #eff6ff;
    --success: #15803d;
    --success-light: #dcfce7;
    --danger: #b91c1c;
    --danger-light: #fee2e2;
    --warning: #a16207;
    --warning-light: #fef3c7;
    --text: #1e293b;
    --muted: #64748b;
    --border: #e2e8f0;
    --background: #f8fafc;

    width: 100%;
    color: var(--text);
    text-align: left;
    box-sizing: border-box;
}

.os-page *,
.os-page *::before,
.os-page *::after {
    box-sizing: border-box;
}

.page-toolbar {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 16px;
    margin-bottom: 16px;
}

.page-toolbar h2 {
    margin: 0;
    font-size: 1.45rem;
}

.page-subtitle {
    margin: 4px 0 0;
    color: var(--muted);
    font-size: 0.9rem;
}

.toolbar-actions {
    display: flex;
    align-items: center;
    gap: 8px;
}

.toolbar-actions button.btn-small {
    width: auto;
    min-width: 90px;
    height: 34px;
    padding: 5px 16px;
}

.toolbar-actions button.btn-photos {
    color: #fff;
    background: var(--primary);
    box-shadow: none;
}

.toolbar-actions button.btn-photos:hover {
    color: #fff;
    background: #1d4ed8;
    box-shadow: none;
}

.os-summary-card,
.content-card {
    background: #fff;
    border: 1px solid var(--border);
    border-radius: 12px;
    box-shadow: 0 2px 8px rgba(15, 23, 42, 0.05);
}

.os-summary-card {
    padding: 14px 18px;
    margin-bottom: 18px;
}

.summary-main {
    display: grid;
    grid-template-columns: minmax(280px, 1fr) auto;
    align-items: center;
    gap: 20px;
    padding-bottom: 12px;
    border-bottom: 1px solid var(--border);
}

.service-summary {
    min-width: 0;
}

.summary-label,
.event-label {
    display: block;
    color: var(--muted);
    font-size: 0.68rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
}

.service-title-row {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 3px;
}

.service-title-row h3 {
    margin: 0;
    font-size: 1.18rem;
    line-height: 1.25;
}

.status-chip {
    display: inline-flex;
    align-items: center;
    min-height: 23px;
    padding: 3px 9px;
    color: #166534;
    background: #dcfce7;
    border-radius: 999px;
    font-size: 0.67rem;
    font-weight: 700;
    white-space: nowrap;
}

.summary-metrics {
    display: grid;
    grid-template-columns: repeat(4, minmax(92px, auto));
    border: 1px solid var(--border);
    border-radius: 9px;
    overflow: hidden;
}

.metric-item {
    min-width: 92px;
    padding: 7px 12px;
    text-align: center;
    background: var(--background);
}

.metric-item + .metric-item {
    border-left: 1px solid var(--border);
}

.metric-item strong {
    display: block;
    margin-top: 2px;
    color: var(--text);
    font-size: 0.88rem;
    white-space: nowrap;
}

.metric-total strong {
    color: var(--success);
}

.summary-meta-grid {
    display: grid;
    grid-template-columns: 1.25fr 1.25fr 0.8fr 1.7fr;
    align-items: start;
    gap: 14px 22px;
    padding-top: 11px;
}

.meta-item {
    min-width: 0;
}

.meta-item > strong,
.location-content strong {
    display: block;
    margin-top: 3px;
    color: var(--text);
    font-size: 0.82rem;
    line-height: 1.25;
    overflow-wrap: anywhere;
}

.location-content {
    display: flex;
    align-items: baseline;
    flex-wrap: wrap;
    gap: 2px 8px;
    margin-top: 3px;
}

.coordinates {
    display: block;
    margin-top: 2px;
    color: var(--muted);
    font-size: 0.68rem;
}

.map-link {
    color: var(--primary);
    font-size: 0.74rem;
    font-weight: 600;
    text-decoration: none;
    white-space: nowrap;
}

.map-link:hover {
    text-decoration: underline;
}

.execution-dates {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-top: 3px;
}

.execution-dates > div {
    min-width: 0;
}

.execution-dates small {
    display: block;
    color: var(--muted);
    font-size: 0.66rem;
}

.execution-dates strong {
    display: block;
    margin-top: 1px;
    font-size: 0.78rem;
    line-height: 1.2;
    white-space: nowrap;
}

.execution-arrow {
    color: #94a3b8;
    font-size: 1rem;
}

.item-total {
    color: var(--success);
}

.content-columns {
    display: grid;
    grid-template-columns: minmax(320px, 0.9fr) minmax(500px, 1.4fr);
    align-items: start;
    gap: 18px;
}

.content-card {
    min-width: 0;
    overflow: hidden;
    text-align: left;
}

.card-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 12px;
    padding: 16px 18px;
    background: var(--background);
    border-bottom: 1px solid var(--border);
}

.card-header > div {
    min-width: 0;
    text-align: left;
}

.card-header h3 {
    margin: 0;
    font-size: 1.05rem;
}

.card-header p {
    margin: 3px 0 0;
    color: var(--muted);
    font-size: 0.8rem;
}

.count-badge {
    display: inline-flex;
    justify-content: center;
    align-items: center;
    min-width: 28px;
    height: 28px;
    padding: 0 8px;
    color: var(--primary);
    background: var(--primary-light);
    border-radius: 999px;
    font-size: 0.78rem;
    font-weight: 700;
}

.empty-state {
    width: auto;
    padding: 32px 20px;
    margin: 0;
    border: 0;
    border-radius: 0;
    background: transparent;
    color: var(--muted);
    text-align: center;
}

.timeline {
    padding: 20px 18px;
}

.timeline-item {
    display: grid;
    grid-template-columns: 22px minmax(0, 1fr);
    gap: 8px;
}

.timeline-marker {
    position: relative;
    display: flex;
    justify-content: center;
}

.timeline-dot {
    position: relative;
    z-index: 2;
    width: 12px;
    height: 12px;
    margin-top: 5px;
    background: var(--primary);
    border: 3px solid #dbeafe;
    border-radius: 50%;
    box-sizing: content-box;
}

.timeline-line {
    position: absolute;
    top: 20px;
    bottom: 0;
    width: 2px;
    background: var(--border);
}

.event-card {
    padding: 0 0 22px 4px;
}

.event-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 12px;
}

.event-header strong {
    font-size: 0.9rem;
}

.event-date {
    color: var(--muted);
    font-size: 0.76rem;
    white-space: nowrap;
}

.event-details {
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    gap: 12px;
    padding: 10px 12px;
    margin-top: 8px;
    background: var(--background);
    border: 1px solid var(--border);
    border-radius: 8px;
    font-size: 0.84rem;
}

.event-label {
    margin-bottom: 3px;
    font-size: 0.68rem;
}

.items-table-wrapper {
    width: 100%;
    overflow-x: auto;
}

.items-table {
    width: 100%;
    min-width: 670px;
    border-collapse: collapse;
}

.items-table th,
.items-table td {
    border: 0;
    padding: 12px 14px;
    border-bottom: 1px solid var(--border);
    font-size: 0.84rem;
    text-align: left;
    vertical-align: middle;
}

.items-table th {
    color: var(--muted);
    background: #fff;
    font-size: 0.72rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.03em;
}

.items-table tbody tr:hover {
    background: var(--background);
}

.items-table th + th,
.items-table td + td {
    border-left: 1px solid var(--border);
}

.items-table tbody tr:last-child td {
    border-bottom: 0;
}

.items-table tfoot td {
    background: var(--background);
    border-top: 2px solid var(--border);
    border-bottom: 0;
    font-weight: 700;
}

.column-code {
    width: 80px;
}

.column-number,
.column-money {
    text-align: right !important;
    white-space: nowrap;
}

.product-code {
    display: inline-flex;
    justify-content: center;
    min-width: 30px;
    padding: 4px 7px;
    color: #475569;
    background: #e2e8f0;
    border-radius: 6px;
    font-size: 0.75rem;
    font-weight: 700;
}

.product-name {
    font-size: 0.84rem;
}

/* Responsividade somente do conteúdo da página; não alcança o modal. */
@media (max-width: 1250px) {
    .summary-main {
        grid-template-columns: 1fr;
        gap: 10px;
    }

    .summary-metrics {
        grid-template-columns: repeat(4, 1fr);
    }

    .summary-meta-grid {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    .content-columns {
        grid-template-columns: 1fr;
    }
}

@media (max-width: 700px) {
    .page-toolbar {
        align-items: stretch;
        flex-direction: column;
    }

    .toolbar-actions {
        justify-content: flex-end;
    }

    .os-summary-card {
        padding: 12px;
    }

    .summary-metrics {
        grid-template-columns: repeat(2, 1fr);
    }

    .metric-item:nth-child(3) {
        border-left: 0;
        border-top: 1px solid var(--border);
    }

    .metric-item:nth-child(4) {
        border-top: 1px solid var(--border);
    }

    .summary-meta-grid {
        grid-template-columns: 1fr;
        gap: 11px;
    }

    .execution-dates {
        flex-wrap: wrap;
    }

    .items-table {
        min-width: 670px;
    }
}


.modal-overlay {
    position: fixed;
    inset: 0;
    z-index: 1999;
    background: rgba(15, 23, 42, 0.55);
    display: flex;
    justify-content: center;
    align-items: center;
}

.photos-modal {
    width: min(1000px, 94vw);
    max-height: 90vh;
    overflow: auto;
    background: #fff;
    border-radius: 10px;
    padding: 16px;
}

.modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 14px;
}

.photos-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
    gap: 10px;
    margin-bottom: 16px;
}

.photo-thumb {
    width: 100%;
    height: 100px;
    object-fit: cover;
    border-radius: 8px;
    border: 2px solid #e2e8f0;
    cursor: pointer;
}

.photo-thumb-active {
    border-color: #2563eb;
}

.photo-preview {
    text-align: center;
}

.photo-preview img {
    max-width: 100%;
    max-height: 65vh;
    object-fit: contain;
    border-radius: 8px;
}
</style>
